import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import MessageRow from "./MessageRow";

const PIN_THRESHOLD_PX = 80;

const ChatThread = observer(() => {
  const endRef = useRef(null);
  const rootRef = useRef(null);
  // Pinned = the user is at (or near) the bottom, so the thread follows the
  // stream. Scrolling up unpins: nothing yanks the viewport while reading —
  // a "jump to latest" pill offers the way back instead.
  const pinned = copilotStore.threadPinned;
  const { messages } = copilotStore;

  // Follow the stream: re-render fires on every token delta (observer), and
  // this key changes with both new messages and growing text blocks.
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const scrollKey = lastMessage
    ? `${messages.length}_${(lastMessage.blocks || []).reduce(
        (size, block) => size + (block.markdown ? block.markdown.length : 1),
        0
      )}`
    : "empty";

  // The overflow container is the parent (.copilotPage__thread) — observe its
  // scroll position to know whether the user has left the bottom.
  useEffect(() => {
    const container = rootRef.current ? rootRef.current.parentElement : null;
    if (!container) return undefined;
    const onScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      copilotStore.setThreadPinned(distanceFromBottom < PIN_THRESHOLD_PX);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [messages.length > 0]);

  useEffect(() => {
    if (pinned && endRef.current) {
      // auto, not smooth: smooth-scrolling on every streamed token stutters
      endRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
  }, [scrollKey, pinned]);

  // The jump pill lives at the thread/composer boundary (Copilot.js) and
  // signals through the store.
  useEffect(() => {
    if (copilotStore.threadJumpRequest && endRef.current) {
      copilotStore.setThreadPinned(true);
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [copilotStore.threadJumpRequest]);

  if (!messages.length) {
    return null; // hero layout is rendered by Copilot.js
  }

  return (
    <div className="copilotThread" ref={rootRef}>
      <div className="copilotThread__inner">
        {messages.map((message, index) => {
          // Rule a divider when the conversation moves to a different section.
          // Only user turns carry one -- the answer belongs to the question
          // above it, so a divider between them would split a single exchange.
          const previous = messages
            .slice(0, index)
            .reverse()
            .find(item => item.section);
          const moved =
            message.section && previous && previous.section !== message.section;
          return (
            <React.Fragment key={message.id}>
              {moved && (
                <div className="copilotThread__divider" role="separator">
                  <span>{message.sectionLabel || message.section}</span>
                </div>
              )}
              <MessageRow message={message} />
            </React.Fragment>
          );
        })}
        <div ref={endRef} />
      </div>
    </div>
  );
});

export default ChatThread;
